import React, {useEffect, useState, useCallback} from 'react'
import {useReduxAuth} from '../../../auth'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {toast} from 'react-toastify'
import DataTableList from '../DataTableList'
import {TIME_TRACKED_HEADERS} from '../../../../constants/RoleConstants'
import {useParams} from 'react-router-dom'
import DeleteConfirmationModal from '../modals/DeleteConfirmationModal'
import EditPatientTrackedTime from '../modals/EditPatientTrackedTime'
import {TimeTrackedValues} from '../forms/ManualTimeTrackedForm'
import {useTimeTrack} from '../../context/TimeTrackedContext'
// Redux imports for refreshing patient data after time tracking operations
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../../../store/store'
import { refreshPatientAfterDeviceChange } from '../../../../../store/slices/patientSlice'
import { USER_ROLES } from '../../../../constants/StatusConstants'
import { useMADoctorDropdown } from '../../context/MADoctorDropdownContext'

export function PatientTimeTracked() {
  const {patientId} = useParams<{patientId: string}>()
  const {currentUser} = useReduxAuth()
  const dispatch = useDispatch<AppDispatch>()
  const roleId = currentUser?.role_id
  
  // MA Doctor Dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined

  // time-track context
  const {timeTrackedData, setTimeTrackedData} = useTimeTrack()

  const notifyError = (msg: string) => toast.error(msg)
  const notifySuccess = (msg: string) => toast.success(msg)

  const [loading, setLoading] = useState(false)

  // — Add-entry modal state —
  const [showAddModal, setShowAddModal] = useState(false)

  // — Edit-entry modal state —
  const [showEditModal, setShowEditModal] = useState(false)
  const [rowToEdit, setRowToEdit] = useState<any>(null)

  // — Delete-confirmation state —
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [rowToDelete, setRowToDelete] = useState<any>(null)

  // <--- stable fetch function
  const fetchEntries = useCallback(async () => {
    setLoading(true)
    try {
      // For MA, use selected doctor's ID; otherwise use current user's ID
      const userId = roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? maSelectedDoctorId
        : currentUser.id
      
      // Only pass userRole if it's defined and not 'undefined'
      const userRole = currentUser.userRole && currentUser.userRole !== 'undefined' 
        ? currentUser.userRole 
        : undefined
      
      // For MA, pass doctor_id as query parameter
      const doctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? maSelectedDoctorId
        : undefined
      
      const resp = await axios.get(
        API_ENDPOINTS.GET_TIME_TRACKED_ENTRIES_OF_PATIENT(
          userId,
          patientId!,
          userRole,
          doctorId
        )
      )
      setTimeTrackedData(resp.data.data.timeTrackedEntries || [])
    } catch (err: any) {
      notifyError(err?.response?.data?.message || 'Could not load time entries')
    } finally {
      setLoading(false)
    }
  }, [currentUser.id, currentUser.userRole, patientId, roleId, maSelectedDoctorId])

  useEffect(() => {
    fetchEntries()
  }, [fetchEntries])

  // — Handlers for Add —
  const handleAddClick = () => setShowAddModal(true)
  const handleAddCancel = () => setShowAddModal(false)

  // <-- call fetchEntries after create
  const handleAddSubmit = async (values: TimeTrackedValues) => {
    try {
      // For MA, use selected doctor's ID; otherwise use current user's ID
      const userId = roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? maSelectedDoctorId
        : currentUser.id
      await axios.post(API_ENDPOINTS.CREATE_TIME_TRACKED_ENTRY(userId, patientId!), values)
      notifySuccess('Time entry added!')
      await fetchEntries()
      setShowAddModal(false)
      
      // Refresh Redux patient state to update PatientDetailBadges
      if (patientId) {
        const doctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maSelectedDoctorId : undefined
        dispatch(refreshPatientAfterDeviceChange({ patientUuid: patientId, doctorId }))
      }
    } catch (err: any) {
      notifyError(err?.response?.data?.message || 'Failed to add entry')
    }
  }

  // — Handlers for Edit —
  const handleAction = (action: {name: string}, row: any) => {
    if (action.name === 'Edit') {
      setRowToEdit(row)
      setShowEditModal(true)
    }
    if (action.name === 'Delete') {
      setRowToDelete(row)
      setShowDeleteModal(true)
    }
  }

  const handleSaveEdit = async (updatedData: any) => {
    if (!rowToEdit) return
    try {
      await axios.put(API_ENDPOINTS.UPDATE_TIME_TRACKED_ENTRY(rowToEdit.id), updatedData)
      notifySuccess('Time entry updated!')
      await fetchEntries()
      
      // Refresh Redux patient state to update PatientDetailBadges
      if (patientId) {
        const doctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maSelectedDoctorId : undefined
        dispatch(refreshPatientAfterDeviceChange({ patientUuid: patientId, doctorId }))
      }
    } catch (err: any) {
      notifyError(err?.response?.data?.message || 'Update failed')
    } finally {
      setShowEditModal(false)
      setRowToEdit(null)
    }
  }
  const handleCancelEdit = () => {
    setShowEditModal(false)
    setRowToEdit(null)
  }

  // — Handlers for Delete —
  const handleConfirmDelete = async () => {
    if (!rowToDelete) return
    try {
      await axios.delete(API_ENDPOINTS.DELETE_TIME_TRACKED_ENTRY(rowToDelete.id))
      notifySuccess('Time entry deleted!')
      await fetchEntries()
      
      // Refresh Redux patient state to update PatientDetailBadges
      if (patientId) {
        const doctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maSelectedDoctorId : undefined
        dispatch(refreshPatientAfterDeviceChange({ patientUuid: patientId, doctorId }))
      }
    } catch (err: any) {
      notifyError(err?.response?.data?.message || 'Delete failed')
    } finally {
      setShowDeleteModal(false)
      setRowToDelete(null)
    }
  }
  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setRowToDelete(null)
  }

  // Function to filter headers based on user permissions
  const filteredHeaders = TIME_TRACKED_HEADERS.filter((header) =>
    header.name !== 'Patient' // Exclude Patient column since we're on patient page
  )

  return (
    <>
      <div className='card mb-5 mb-xl-10' id='kt_profile_details_view'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h3 className='fw-bolder m-0'>Time Tracked</h3>
        </div>

        <DataTableList
          showAddButton={false}
          showHeader={false}
          columns={filteredHeaders}
          data={timeTrackedData}
          isLoading={loading}
          actions={[
            {name: 'Edit', title: 'Edit'},
            {name: 'Delete', title: 'Delete'},
          ]}
          onAction={handleAction}
        />
      </div>

      {/* Timer-driven add form */}
      {/* <TimerComponent onEntryAdded={fetchEntries} /> */}

      {/* Edit Modal */}
      <EditPatientTrackedTime
        show={showEditModal}
        initialData={rowToEdit}
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmationModal
        title='Delete Time Entry'
        description="This action cannot be undone. It will permanently delete the time entry and remove all associated data."
        show={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  )
}
